import { SyntheticEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import {
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { joinClassNames } from "@utils/helpers";
import { useThemeContext } from "@utils/hooks";
import { useIsShopsite } from "@context/DomainContext";
import LoadingSpinner from "@components/loadingSpinner";
import { Heading } from "@components/theming";
import EventToolTip from "@features/eventPage/components/eventToolTip";

interface Props {
  title: string;
  description: string;
  status: "loading" | "success" | "failure" | "complete" | null;
  defaultValue?: string;
  onSubmit: (password: string) => void;
  name: string;
  className?: string;
}

const PasswordForm = ({
  title,
  description,
  status,
  defaultValue,
  onSubmit,
  name,
  className,
}: Props) => {
  const isShopsite = useIsShopsite();
  const theme = useThemeContext();
  const isDark = theme && theme.appearance === "dark";

  return (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "bg-white sm:rounded-lg" : "",
        className ?? ""
      )}
    >
      <div
        className={joinClassNames(
          "relative z-10 flex flex-col items-center justify-center gap-1 w-full h-[197px] p-4 overflow-hidden",
          isShopsite
            ? "bg-white/40 sm:rounded-lg border border-gray-200 backdrop-blur-3xl"
            : "bg-[color:var(--gray-1)] sm:rounded-[var(--radius-5)] border border-[color:var(--gray-a7)]"
        )}
      >
        <div className="flex items-center gap-1 mb-1">
          <Heading
            as="h2"
            size="6"
            weight="bold"
            shopsiteClasses="font-black text-2xl"
          >
            {title}
          </Heading>
          <EventToolTip
            title={title}
            description={description}
            icon={
              <LockClosedIcon className="absolute top-0 left-4 w-24 h-24 text-white/10 -rotate-12" />
            }
          />
        </div>
        <form
          id={`${name}_form`}
          onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
            e.preventDefault();
            const password = new FormData(e.currentTarget).get(name);
            if (password) {
              onSubmit(password as string);
            }
          }}
        >
          <motion.div
            initial={{ x: 0 }}
            animate={
              status === "failure"
                ? { x: [-5, 5, -5, 5, -4, 4, -3, 3, 0] }
                : { x: 0 }
            }
            transition={{ duration: 0.4 }}
            className={joinClassNames(
              "flex items-center gap-1 border px-2 py-1.5 ring-2 focus-within:outline-none transition-[box-shadow] ease-in-out duration-200",
              isShopsite
                ? "rounded-md border-gray-300 bg-white text-black"
                : "rounded-[var(--radius-3)] border-[color:var(--gray-a7)] text-[color:var(--gray-12)]",
              isDark ? "bg-[color:var(--gray-1)]" : "bg-white",
              status === "failure"
                ? "border-transparent ring-red-500"
                : isShopsite
                ? "focus-within:border-transparent ring-transparent focus-within:ring-indigo-500"
                : "focus-within:border-transparent ring-transparent focus-within:ring-[color:var(--accent-9)]"
            )}
          >
            <input
              id={`${name}_input`}
              defaultValue={defaultValue ?? ""}
              name={name}
              type="password"
              required
              aria-label={title}
              aria-invalid={status === "failure"}
              className={joinClassNames(
                "h-8 focus-visible:outline-none",
                isDark ? "bg-[color:var(--gray-1)]" : "bg-white"
              )}
            />
            <button
              type="submit"
              disabled={!!status}
              className={joinClassNames(
                "relative flex p-1 items-center justify-center",
                isShopsite ? "rounded-full" : "rounded-[var(--radius-6)]",
                isShopsite
                  ? status === "loading"
                    ? "bg-gray-200"
                    : status === "success"
                    ? "bg-emerald-100"
                    : status === "failure"
                    ? "bg-red-100"
                    : "bg-gray-100"
                  : status === "loading"
                  ? "bg-[color:var(--gray-4)]"
                  : status === "success"
                  ? "bg-[color:var(--green-3)]"
                  : status === "failure"
                  ? "bg-[color:var(--red-3)]"
                  : "bg-[color:var(--gray-3)]"
              )}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={status}
                  initial={{ opacity: 0, width: "auto" }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: "auto" }}
                  transition={{ duration: 0.15 }}
                >
                  {status === "loading" ? (
                    <LoadingSpinner
                      size="24"
                      classes="p-0.5"
                      color={isShopsite ? undefined : "var(--gray-11)"}
                    />
                  ) : status === "success" ? (
                    <CheckIcon
                      className={joinClassNames(
                        "h-6 w-6",
                        isShopsite
                          ? "text-emerald-800"
                          : "text-[color:var(--green-10)]"
                      )}
                    />
                  ) : status === "failure" ? (
                    <XMarkIcon
                      className={joinClassNames(
                        "h-6 w-6",
                        isShopsite
                          ? "text-red-800"
                          : "text-[color:var(--red-10)]"
                      )}
                    />
                  ) : (
                    <ArrowRightIcon
                      className={joinClassNames(
                        "h-6 w-6",
                        isShopsite
                          ? "text-gray-800"
                          : "text-[color:var(--gray-10)]"
                      )}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
              <motion.div
                className="absolute top-0 right-0"
                animate={
                  status === "success"
                    ? {
                        scale: [0, 1, 1, 1, 0],
                        x: [0, 8, 14, 20, 22],
                        y: [0, -8, -14, -16, -14],
                        rotate: [0, 10, 20, 30, 40],
                      }
                    : { scale: 0, x: 0, y: 0, rotate: 0 }
                }
              >
                {status === "success" ? (
                  <SparklesIcon className="text-yellow-400 h-4 w-4" />
                ) : null}
              </motion.div>
              <motion.div
                className="absolute bottom-0 -left-1"
                animate={
                  status === "success"
                    ? {
                        scale: [0, 1, 1, 1, 0],
                        x: [0, -8, -14, -20, -22],
                        y: [0, -4, -6, -2, 2],
                        rotate: [0, -10, -20, -30, -40],
                      }
                    : { scale: 0, x: 0, y: 0, rotate: 0 }
                }
              >
                {status === "success" ? (
                  <SparklesIcon className="text-yellow-400 h-4 w-4" />
                ) : null}
              </motion.div>
            </button>
          </motion.div>
        </form>
      </div>
      {isShopsite ? (
        <>
          <span className="absolute top-1 right-8 w-14 h-32 rounded-full bg-yellow-400" />
          <span className="absolute top-8 right-12 w-32 h-14 rounded-full bg-fuchsia-400" />
          <span className="absolute bottom-2 right-8 w-32 h-14 rounded-full bg-sky-400" />
          <span className="absolute bottom-5 left-5 w-16 h-16 rounded-full bg-emerald-400" />
        </>
      ) : null}
    </div>
  );
};

export default PasswordForm;
